import React, { createContext, useReducer, useEffect, useState } from "react";
import CONSTANTS from "../Constants/ApiConstants";
import { UserLoginModal } from "../Pages/Login";
import Axios from "axios";
import { useNavigate } from "react-router-dom";

// Initial State
const initialState = {
    userId: localStorage.getItem(CONSTANTS.STORE.USER_ID) || null,
    token: localStorage.getItem(CONSTANTS.STORE.USER_TOKEN) || null,
    mobileNo: null,
    user: null,
    membership: null,
    isLoading: false,
};

// Actions
const ACTIONS = {
    SET_AUTH: "SET_AUTH",
    CLEAR_AUTH: "CLEAR_AUTH",
    SET_USER_DETAILS: "SET_USER_DETAILS",
    SET_LOADING: "SET_LOADING",
};

// Reducer
function authReducer(state, action) {
    switch (action.type) {
        case ACTIONS.SET_AUTH:
            return {
                ...state,
                token: action.payload.token,
                userId: action.payload.userId,
            };
        case ACTIONS.CLEAR_AUTH:
            return {
                userId: null,
                token: null,
                mobileNo: null,
                user: null,
                membership: [],
            };
        case ACTIONS.SET_USER_DETAILS:
            return {
                ...state,
                mobileNo: action.payload.mobileNo,
                user: action.payload.user,
                membership: action.payload.membership,
            };
        case ACTIONS.SET_LOADING:
            return {
                ...state,
                isLoading: action.payload,
            };
        default:
            return state;
    }
}

// Create Context
export const AuthContext = createContext({
    userId: localStorage.getItem(CONSTANTS.STORE.USER_ID) || null,
    token: localStorage.getItem(CONSTANTS.STORE.USER_TOKEN) || null,
    mobileNo: null,
    user: null,
    membership: null,
    isLoginModal: false,
    setIsLoginModal: () => { /* Mock function */ },
    signIn: () => { /* Mock function */ },
    signOut: () => { /* Mock function */ },
    getUserDetails: () => { /* Mock function */ },
    isLoading: true,
    setIsLoading: () => { /* Mock function */ },
});

// Provider
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const navigate = useNavigate();

    const [isLoginModal, setIsLoginModal] = useState(false);

    // Persist token & userId in localStorage whenever they change
    useEffect(() => {
        if (state.token) {
            setIsLoading(true);
            localStorage.setItem(CONSTANTS.STORE.USER_TOKEN, state.token);
        } else {
            setIsLoading(true);
            localStorage.removeItem(CONSTANTS.STORE.USER_TOKEN);
        }

        if (state.userId) {
            localStorage.setItem(CONSTANTS.STORE.USER_ID, state.userId);
        } else {
            localStorage.removeItem(CONSTANTS.STORE.USER_ID);
        }
    }, [state.token, state.userId]);

    // Auto-fetch user details on app load if token exists
    useEffect(() => {
        const fetchUserDetails = async () => {
            if (!state.token) return;

            await setIsLoading(true);

            try {
                const response = await Axios({
                    method: "GET",
                    url: CONSTANTS.API_URL + `user/info`,
                    headers: { Authorization: `${state.token}` },
                });

                if (!([200, 201].includes(response.status))) throw new Error("Failed to fetch user details");

                const responseData = (response?.data || {});

                if (responseData.status) {
                    dispatch({
                        type: ACTIONS.SET_USER_DETAILS,
                        payload: {
                            user: { ...state.user, ...responseData.data },
                            membership: responseData?.data?.subscription || null,
                        },
                    });
                }
            } catch (error) {
                const statusCode = error?.status || "";
                // console.log(statusCode);

                if (statusCode === 401) {
                    dispatch({ type: ACTIONS.CLEAR_AUTH }); // optional: log out on error
                } else {
                    console.log("Error fetching user details:", error);
                }

            } finally {
                setIsLoading(false);
            }
        };

        fetchUserDetails();
    }, [state.token, state.userId]);

    // Functions
    const signIn = ({ token, userId, user }) => {
        // console.log(`🚀 ~ signIn ~ { token, userId, user }:`, { token, userId, user })
        dispatch({ type: ACTIONS.SET_AUTH, payload: { token, userId: user?.id || "" } });
        dispatch({
            type: ACTIONS.SET_USER_DETAILS,
            payload: {
                mobileNo: user.mobile_no,
                user: user,
                membership: user.membership || null,
            },
        });
    };

    const setIsLoading = (loading) => {
        dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
    }

    const signOut = () => {
        dispatch({ type: ACTIONS.CLEAR_AUTH });
        navigate({ to: "/", replace: true });
    };

    const getUserDetails = async () => {
        if (!state.token) return;

        // await setIsLoading(true);

        try {
            const response = await Axios({
                method: "GET",
                url: CONSTANTS.API_URL + `user/info`,
                headers: { Authorization: `${state.token}` },
            });

            if (!([200, 201].includes(response.status))) throw new Error("Failed to fetch user details");

            const responseData = (response?.data || {});

            if (responseData.status) {
                dispatch({
                    type: ACTIONS.SET_USER_DETAILS,
                    payload: {
                        user: { ...state.user, ...responseData.data },
                        membership: responseData?.data?.membership || [],
                    },
                });
            }
        } catch (error) {
            const statusCode = error?.status || "";
            // console.log(statusCode);

            if (statusCode === 401) {
                dispatch({ type: ACTIONS.CLEAR_AUTH }); // optional: log out on error
            } else {
                console.log("Error fetching user details:", error);
            }

        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                signIn,
                signOut,
                getUserDetails,
                isLoginModal,
                setIsLoginModal,
                setIsLoading
            }}
        >
            {children}
            <UserLoginModal />
        </AuthContext.Provider>
    );
};
